import React from 'react';
import { Button, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { formatMoneyPl } from '../../../utils/currency';
import { formatUnit } from '../../../utils/units';
import { formatLocalizedNumber } from '../../../utils/numberFormatting';
import type { FreightLineRow, FreightLinesTotals, LoadUnit } from '../freightGoodsTable';

const { Text } = Typography;

const UNITS: LoadUnit[] = ['pcs', 'm2', 'm3'];

const formatQuantity = (value: number, fractionDigits: number): string =>
  formatLocalizedNumber(value, fractionDigits, 'pl');

interface FreightOrderCardProps {
  /** Test-id stem, e.g. `transport-supplier-freight` → `…-card`, `…-line`, `…-total`, `…-average`. */
  testIdPrefix: string;
  label: string;
  partyName: string | null;
  onOpen: () => void;
  /** Extra tags after the party (e.g. „Odbiór własny klienta”). */
  tags?: React.ReactNode;
  /** Header share; `null` hides it. The amount is shown only with several orders on the truck. */
  share: { percent: number | null; amountPLN: number | null } | null;
  showShareAmount: boolean;
  lines: FreightLineRow[];
  totals: FreightLinesTotals | null;
  /** Table shown when true; otherwise `emptyContent` (no saved share, no cargo lines). */
  showTable: boolean;
  emptyContent?: React.ReactNode;
  /**
   * Text for an unknown share percent, unit cost or a line without units.
   * ZD cards say „brak danych” (default), ZK cards keep their original „—”.
   * An unknown line cost always reads „brak danych”.
   */
  missingText?: string;
  /**
   * Usability round 2026-10-05: the truck's currency, its PLN rate (saved FX
   * snapshot; 1 for PLN) and the truck's total in PLN — each line then shows
   * its share of the truck and its cost (and cost per unit) in the truck
   * currency next to PLN. Display only: the PLN figures stay the backend's.
   */
  truck?: { currency: string; rate: number | null; totalPLN: number | null } | null;
}

const toTruckCurrency = (valuePLN: number | null, rate: number | null): number | null => (
  valuePLN === null || rate === null || !(rate > 0) ? null : Math.round((valuePLN / rate) * 100) / 100
);

/**
 * One order's card in a transport freight section — ZD (INBOUND) or ZK
 * (OUTBOUND). Presentation of the shared goods table (`freightGoodsTable.ts`):
 * goods with dimensions and quantities, each line's backend cost and its cost
 * per szt./m²/m³, then "Razem" / "Średnio". Render only with
 * `canViewTransportCosts`.
 */
const FreightOrderCard: React.FC<FreightOrderCardProps> = ({
  testIdPrefix,
  label,
  partyName,
  onOpen,
  tags,
  share,
  showShareAmount,
  lines,
  totals,
  showTable,
  emptyContent,
  missingText,
  truck = null,
}) => {
  const { t } = useTranslation(['transport']);
  const unknown = t('transport:supplierOrders.freight.unknown');
  const missing = missingText ?? unknown;
  const foreign = Boolean(truck && truck.currency !== 'PLN');
  const pln = (value: number | null) => (value === null ? unknown : formatMoneyPl(value, 'PLN'));
  // „12,00 EUR · 51,60 zł” on a foreign-currency truck, PLN alone otherwise.
  const both = (valuePLN: number | null, fallback: string) => {
    if (valuePLN === null) return fallback;
    const inTruck = foreign ? toTruckCurrency(valuePLN, truck!.rate) : null;
    return inTruck === null ? formatMoneyPl(valuePLN, 'PLN') : `${formatMoneyPl(inTruck, truck!.currency)} · ${formatMoneyPl(valuePLN, 'PLN')}`;
  };
  const lineShare = (valuePLN: number | null) => (
    valuePLN === null || !truck?.totalPLN ? '—' : `${formatQuantity((valuePLN / truck.totalPLN) * 100, 2)}%`
  );
  const plnOrMissing = (value: number | null) => (value === null ? missing : formatMoneyPl(value, 'PLN'));
  const quantityText = (unit: LoadUnit, quantity: number) =>
    `${formatQuantity(quantity, unit === 'pcs' ? 0 : 3)} ${formatUnit(unit)}`;

  return (
    <article className="transport-detail-zd-cost" data-testid={`${testIdPrefix}-card`}>
      <div className="transport-detail-zd-cost__head">
        <div>
          <Button type="link" size="small" className="transport-detail-zd-cost__link" onClick={onOpen}>
            {label}
          </Button>
          <Text type="secondary"> · {partyName || '—'}</Text>
          {tags}
        </div>
        {share ? (
          <Text>
            {t('transport:supplierOrders.freight.share')}{' '}
            <strong>{share.percent === null ? missing : `${formatQuantity(share.percent, 2)}%`}</strong>
            {showShareAmount || truck ? ` · ${both(share.amountPLN, unknown)}` : null}
          </Text>
        ) : null}
      </div>
      {showTable ? (
        <table className="transport-detail-zd-cost__table transport-detail-zd-cost__table--goods">
          <thead>
            <tr>
              <th>{t('transport:supplierOrders.freight.goods')}</th>
              <th>{t('transport:supplierOrders.freight.quantity')}</th>
              {truck ? <th>{t('transport:supplierOrders.freight.lineShare')}</th> : null}
              <th>{t('transport:supplierOrders.freight.lineCost')}</th>
              <th>{t('transport:supplierOrders.freight.perPcs')}</th>
              <th>{t('transport:supplierOrders.freight.perM2')}</th>
              <th>{t('transport:supplierOrders.freight.perM3')}</th>
            </tr>
          </thead>
          <tbody>
            {lines.map((line) => (
              <tr key={line.id} data-testid={`${testIdPrefix}-line`}>
                <td>
                  <strong>{line.name}</strong>
                  {line.dimensions ? (
                    <Text type="secondary" className="transport-detail-zd-line__dimensions">{line.dimensions}</Text>
                  ) : null}
                </td>
                <td>
                  {line.units.length > 0
                    ? line.units.map((found) => <div key={found.unit}>{quantityText(found.unit, found.quantity)}</div>)
                    : missing}
                </td>
                {truck ? <td data-testid={`${testIdPrefix}-line-share`}>{lineShare(line.costPLN)}</td> : null}
                <td><strong>{both(line.costPLN, unknown)}</strong></td>
                {UNITS.map((unit) => {
                  // A unit the product has no stored equivalent for is simply not offered.
                  const found = line.units.find((candidate) => candidate.unit === unit);
                  return <td key={unit}>{found ? (truck ? both(found.costPerUnit, missing) : plnOrMissing(found.costPerUnit)) : '—'}</td>;
                })}
              </tr>
            ))}
          </tbody>
          {totals ? (
            <tfoot>
              <tr data-testid={`${testIdPrefix}-total`}>
                <td>{t('transport:supplierOrders.freight.total')}</td>
                <td>
                  {UNITS.flatMap((unit) => {
                    const quantity = totals[unit].quantity;
                    return quantity === null ? [] : [<div key={unit}>{quantityText(unit, quantity)}</div>];
                  })}
                </td>
                {/* Same column order as the header: Ilość, then Udział w aucie. */}
                {truck ? <td>{lineShare(totals.costPLN)}</td> : null}
                <td><strong>{both(totals.costPLN, unknown)}</strong></td>
                <td />
                <td />
                <td />
              </tr>
              <tr data-testid={`${testIdPrefix}-average`}>
                <td>{t('transport:supplierOrders.freight.average')}</td>
                <td />
                {truck ? <td /> : null}
                <td />
                {UNITS.map((unit) => {
                  const average = totals[unit].average;
                  return <td key={unit}>{average === null ? '—' : (truck ? both(average, '—') : pln(average))}</td>;
                })}
              </tr>
            </tfoot>
          ) : null}
        </table>
      ) : emptyContent}
    </article>
  );
};

export default FreightOrderCard;
